import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  IconButton,
  Button,
  FormControl,
  Divider,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

const defaultRow = () => ({
  cuenta_id: "",
  cuenta_codigo: "",
  cuenta_nombre: "",
  cantidad: "1",
  valor_unitario: "",
  iva_porcentaje: 15,
  ice_porcentaje: 0,
  descuento_pct: "",
  descuento_valor: "",
  subtotal: "0.00",
});

const sanitizeDecimal = (v) => String(v ?? "").replace(/,/g, ".");

const cleanDecimal2 = (v) => {
  const s = sanitizeDecimal(v);
  if (s === "") return "";
  if (/^\d*(\.\d{0,2})?$/.test(s)) return s;
  const only = s.replace(/[^\d.]/g, "");
  const parts = only.split(".");
  if (parts.length === 1) return parts[0];
  return `${parts[0]}.${parts.slice(1).join("").slice(0, 2)}`;
};

const toNum = (v) => {
  const n = Number(sanitizeDecimal(v));
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n) => Math.round((toNum(n) + 1e-10) * 100) / 100;

function RowAccountSearch({ value, onPick, disabled }) {
  const [inputValue, setInputValue] = useState(value?.label || "");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setInputValue(value?.label || "");
  }, [value?.id]);

  useEffect(() => {
    const q = String(inputValue || "").trim();
    if (q.length < 2) {
      setOptions([]);
      setLoading(false);
      return;
    }

    const ctrl = new AbortController();
    let mounted = true;

    setLoading(true);

    const t = setTimeout(async () => {
      try {
        const res = await fetch(`/cuentas/search-accounts/?q=${encodeURIComponent(q)}`, {
          method: "GET",
          credentials: "include",
          cache: "no-store",
          signal: ctrl.signal,
          headers: { Accept: "application/json" },
        });

        const json = await res.json().catch(() => ({}));
        if (!mounted) return;

        const results = Array.isArray(json.results) ? json.results : [];
        setOptions(
          results.map((a) => ({
            id: a.id,
            codigo: a.codigo,
            nombre: a.nombre,
            label: `${a.codigo} - ${a.nombre}`,
          }))
        );
      } catch {
        if (!mounted) return;
        setOptions([]);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    }, 300);

    return () => {
      mounted = false;
      clearTimeout(t);
      ctrl.abort();
    };
  }, [inputValue]);

  return (
    <Autocomplete
      disabled={disabled}
      options={options}
      loading={loading}
      value={value || null}
      inputValue={inputValue}
      onInputChange={(_, v, reason) => {
        if (reason === "reset") return;
        setInputValue(v);
      }}
      onChange={(_, v) => onPick?.(v)}
      isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
      getOptionLabel={(o) => o?.label || ""}
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder=""
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default function InvoiceAccount({ rows = [], setRows = () => {}, onAccountDataChange }) {
  const cbRef = useRef(onAccountDataChange);

  useEffect(() => {
    cbRef.current = onAccountDataChange;
  }, [onAccountDataChange]);

  const safeRows = Array.isArray(rows) ? rows : [];

  useEffect(() => {
    if (!safeRows.length) setRows([defaultRow()]);
  }, [safeRows.length, setRows]);

  const recalcRow = (r) => {
    const qty = Math.max(0, parseInt(r.cantidad || "0", 10) || 0);
    const unit = toNum(r.valor_unitario);
    const base = round2(qty * unit);

    const descPct = toNum(r.descuento_pct);
    const descuento = descPct > 0 ? round2((base * descPct) / 100) : 0;
    const subtotal = round2(base - descuento);

    const ivaPct = [0, 5, 15].includes(Number(r.iva_porcentaje)) ? Number(r.iva_porcentaje) : 15;
    const iva = round2((subtotal * ivaPct) / 100);

    const icePct = toNum(r.ice_porcentaje);
    const ice = round2((subtotal * icePct) / 100);

    return {
      ...r,
      cantidad: String(qty),
      iva_porcentaje: ivaPct,
      descuento_valor: descuento.toFixed(2),
      subtotal: subtotal.toFixed(2),
    };
  };

  const updateRow = (idx, patch) => {
    setRows(
      safeRows.map((r, i) => {
        if (i !== idx) return r;
        return recalcRow({ ...r, ...patch });
      })
    );
  };

  const addRow = () => setRows([...safeRows, defaultRow()]);

  const removeRow = (idx) => {
    const next = safeRows.filter((_, i) => i !== idx);
    setRows(next.length ? next : [defaultRow()]);
  };

  const totals = useMemo(() => {
    return safeRows.reduce(
      (acc, r) => {
        const qty = Math.max(0, parseInt(r.cantidad || "0", 10) || 0);
        const unit = toNum(r.valor_unitario);
        const base = round2(qty * unit);

        const descPct = toNum(r.descuento_pct);
        const descuento = descPct > 0 ? round2((base * descPct) / 100) : 0;
        const subtotal = round2(base - descuento);

        const ivaPct = [0, 5, 15].includes(Number(r.iva_porcentaje)) ? Number(r.iva_porcentaje) : 15;
        const iva = round2((subtotal * ivaPct) / 100);
        const ice = round2((subtotal * toNum(r.ice_porcentaje)) / 100);

        if (ivaPct === 15) acc.sub15 = round2(acc.sub15 + subtotal);
        else if (ivaPct === 5) acc.sub5 = round2(acc.sub5 + subtotal);
        else acc.sub0 = round2(acc.sub0 + subtotal);

        acc.descuento = round2(acc.descuento + descuento);

        if (ivaPct === 15) acc.iva15 = round2(acc.iva15 + iva);
        else if (ivaPct === 5) acc.iva5 = round2(acc.iva5 + iva);

        acc.ice = round2(acc.ice + ice);
        acc.total = round2(acc.total + round2(subtotal + iva + ice));
        return acc;
      },
      { sub15: 0, sub5: 0, sub0: 0, descuento: 0, iva15: 0, iva5: 0, ice: 0, total: 0 }
    );
  }, [safeRows]);

  useEffect(() => {
    cbRef.current?.({
      cuentas: safeRows.map((r) => ({
        cuenta_id: r.cuenta_id,
        cuenta_codigo: r.cuenta_codigo,
        cuenta_nombre: r.cuenta_nombre,
        cantidad: r.cantidad,
        valor_unitario: r.valor_unitario,
        iva_porcentaje: r.iva_porcentaje,
        ice_porcentaje: r.ice_porcentaje,
        descuento_pct: r.descuento_pct,
        descuento_valor: r.descuento_valor,
        subtotal: r.subtotal,
      })),
      subtotal15: totals.sub15,
      subtotal5: totals.sub5,
      subtotal0: totals.sub0,
      descuento_total: totals.descuento,
      iva15: totals.iva15,
      iva5: totals.iva5,
      ice: totals.ice,
      total: totals.total,
      resumen_totales: {
        sub15: totals.sub15,
        sub5: totals.sub5,
        sub0: totals.sub0,
        descuento: totals.descuento,
        iva15: totals.iva15,
        iva5: totals.iva5,
        ice: totals.ice,
        total: totals.total,
      },
    });
  }, [safeRows, totals]);

  return (
    <Box>
      <Paper sx={{ borderRadius: 2, border: "1px solid #e6e6e6" }}>
        <Box sx={{ bgcolor: "#f3f3f3", px: 2, py: 1.25 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={1}>
              <Typography fontWeight={700}>Cant.</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography fontWeight={700}>Cuenta</Typography>
            </Grid>
            <Grid item xs={12} md={1.1}>
              <Typography fontWeight={700}>Valor U.</Typography>
            </Grid>
            <Grid item xs={12} md={1}>
              <Typography fontWeight={700}>IVA</Typography>
            </Grid>
            <Grid item xs={12} md={1}>
              <Typography fontWeight={700}>ICE %</Typography>
            </Grid>
            <Grid item xs={12} md={1.2}>
              <Typography fontWeight={700}>Desc. %</Typography>
            </Grid>
            <Grid item xs={12} md={1.2}>
              <Typography fontWeight={700}>Desc. $</Typography>
            </Grid>
            <Grid item xs={12} md={1.1}>
              <Typography fontWeight={700}>Subtotal</Typography>
            </Grid>
            <Grid item xs={12} md={0.3} />
          </Grid>
        </Box>

        <Box sx={{ p: 2 }}>
          {safeRows.map((r, idx) => {
            const selected = r.cuenta_id
              ? {
                  id: r.cuenta_id,
                  codigo: r.cuenta_codigo,
                  nombre: r.cuenta_nombre,
                  label: `${r.cuenta_codigo} - ${r.cuenta_nombre}`,
                }
              : null;

            return (
              <Grid
                container
                spacing={1.5}
                key={idx}
                alignItems="center"
                sx={{ mb: idx === safeRows.length - 1 ? 0 : 2 }}
              >
                <Grid item xs={12} md={1}>
                  <TextField
                    value={r.cantidad}
                    onChange={(e) => updateRow(idx, { cantidad: e.target.value.replace(/\D/g, "") })}
                    sx={{ width: 70 }}
                    inputProps={{ inputMode: "numeric" }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <RowAccountSearch
                    value={selected}
                    onPick={(acc) => {
                      if (!acc) {
                        updateRow(idx, { cuenta_id: "", cuenta_codigo: "", cuenta_nombre: "" });
                        return;
                      }
                      updateRow(idx, {
                        cuenta_id: acc.id,
                        cuenta_codigo: acc.codigo,
                        cuenta_nombre: acc.nombre,
                      });
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={1.1}>
                  <TextField
                    value={r.valor_unitario}
                    onChange={(e) => updateRow(idx, { valor_unitario: cleanDecimal2(e.target.value) })}
                    sx={{ width: 100 }}
                    inputProps={{ inputMode: "decimal" }}
                  />
                </Grid>

                <Grid item xs={12} md={1}>
                  <FormControl fullWidth>
                    <Select
                      value={[0, 5, 15].includes(Number(r.iva_porcentaje)) ? Number(r.iva_porcentaje) : 15}
                      onChange={(e) => updateRow(idx, { iva_porcentaje: e.target.value })}
                      sx={{ width: 90 }}
                    >
                      <MenuItem value={0}>0%</MenuItem>
                      <MenuItem value={5}>5%</MenuItem>
                      <MenuItem value={15}>15%</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={1}>
                  <TextField
                    value={r.ice_porcentaje}
                    onChange={(e) => updateRow(idx, { ice_porcentaje: cleanDecimal2(e.target.value) })}
                    sx={{ width: 80 }}
                    inputProps={{ inputMode: "decimal" }}
                  />
                </Grid>

                <Grid item xs={12} md={1.2}>
                  <TextField
                    value={r.descuento_pct}
                    onChange={(e) => updateRow(idx, { descuento_pct: cleanDecimal2(e.target.value) })}
                    sx={{ width: 80 }}
                    inputProps={{ inputMode: "decimal" }}
                  />
                </Grid>

                <Grid item xs={12} md={1.2}>
                  <TextField
                    value={r.descuento_valor}
                    disabled
                    sx={{ width: 90 }}
                    inputProps={{ inputMode: "decimal" }}
                  />
                </Grid>

                <Grid item xs={12} md={1.1}>
                  <Typography>${String(r.subtotal ?? "0.00")}</Typography>
                </Grid>

                <Grid item xs={12} md={0.3}>
                  <IconButton onClick={() => removeRow(idx)}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Grid>
              </Grid>
            );
          })}

          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" startIcon={<AddCircleOutlineIcon />} onClick={addRow}>
              Agregar cuenta
            </Button>
          </Box>
        </Box>

        <Divider />
      </Paper>
    </Box>
  );
}
